import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { OversightTone } from "./oversight-data";

const TONE_VARIANT: Record<OversightTone, "success" | "warning" | "danger" | "info" | "pending" | "neutral"> = {
  success: "success",
  warning: "warning",
  danger: "danger",
  info: "info",
  pending: "pending",
  neutral: "neutral",
};

export function ToneBadge({
  tone,
  children,
  className,
  dot = true,
}: {
  tone: OversightTone;
  children: React.ReactNode;
  className?: string;
  dot?: boolean;
}) {
  return (
    <Badge variant={TONE_VARIANT[tone]} dot={dot} className={className}>
      {children}
    </Badge>
  );
}

export function severityTone(severity: string): OversightTone {
  switch (severity) {
    case "Critical":
    case "High":
      return "danger";
    case "Medium":
      return "warning";
    case "Low":
      return "success";
    default:
      return "info";
  }
}

export function statusToneFor(status: string): OversightTone {
  switch (status) {
    case "Open":
      return "danger";
    case "Investigating":
    case "Held":
      return "warning";
    case "Escalated":
      return "danger";
    case "Monitoring":
    case "Acknowledged":
      return "info";
    case "Resolved":
    case "Auto-resolved":
    case "Passed":
    case "Complete":
      return "success";
    case "Running":
      return "info";
    default:
      return "neutral";
  }
}

export function SectionTitle({ children, className }: { children: React.ReactNode; className?: string }) {
  return <h2 className={cn("mb-3 text-[15px] font-bold text-ink", className)}>{children}</h2>;
}

export function Panel({
  title,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn("mb-5 overflow-hidden rounded-lg border border-line bg-surface", className)}>
      {title && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-5 py-3">
          <h3 className="text-[15px] font-bold text-ink">{title}</h3>
          {actions}
        </header>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

export function SegmentedBar({
  segments,
  className,
  height = 8,
}: {
  segments: { label: string; percent?: number; flex?: number; tone: OversightTone }[];
  className?: string;
  height?: number;
}) {
  const TONE_BG: Record<OversightTone, string> = {
    success: "bg-success",
    warning: "bg-warning",
    danger: "bg-danger",
    info: "bg-info",
    pending: "bg-pending",
    neutral: "bg-disabled",
  };
  return (
    <div
      role="img"
      aria-label={segments.map((s) => `${s.label} ${s.percent ?? ""}${s.percent ? "%" : ""}`).join(", ")}
      className={cn("flex w-full overflow-hidden rounded-full", className)}
      style={{ height }}
    >
      {segments.map((s) => (
        <span
          key={s.label}
          className={cn("h-full", TONE_BG[s.tone])}
          style={{ flexGrow: s.flex ?? s.percent ?? 1, flexBasis: 0 }}
        />
      ))}
    </div>
  );
}

export function EventTimeline({
  events,
  className,
}: {
  events: { title: string; meta?: string; detail?: React.ReactNode; tone: OversightTone; ts?: string }[];
  className?: string;
}) {
  const DOT: Record<OversightTone, string> = {
    success: "bg-success",
    warning: "bg-warning",
    danger: "bg-danger",
    info: "bg-info",
    pending: "bg-pending",
    neutral: "bg-disabled",
  };
  return (
    <ol className={cn("relative", className)}>
      {events.map((e, i) => (
        <li key={`${e.title}-${i}`} className="relative flex gap-3 pb-5 last:pb-0">
          {i < events.length - 1 && (
            <span aria-hidden className="absolute left-[5px] top-4 h-full w-px bg-line" />
          )}
          <span aria-hidden className={cn("relative z-10 mt-1.5 size-[11px] shrink-0 rounded-full ring-2 ring-surface", DOT[e.tone])} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-3">
              <span className="text-[13px] font-bold text-ink">{e.title}</span>
              {e.ts && <span className="font-mono text-[11px] text-muted">{e.ts}</span>}
            </div>
            {e.meta && <div className="mt-0.5 text-xs leading-relaxed text-muted">{e.meta}</div>}
            {e.detail && <div className="mt-0.5 text-xs leading-relaxed text-text">{e.detail}</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: React.ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-muted">
        {children}
      </td>
    </tr>
  );
}
